import cn from 'classnames';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import isSwitchingToCMYK from 'editor/src/store/app/selector/isSwitchingToCMYK';
import setDesignColorSpaceOperation from 'editor/src/store/design/operation/setDesignColorSpaceOperation';
import getDesignColorSpace from 'editor/src/store/design/selector/getDesignColorSpace';
import { ColorSpace } from 'editor/src/store/design/types';
import { useDispatch, useSelector } from 'editor/src/store/hooks';
import { VariationProductControl, ProductControlOption, ControlUIType } from 'editor/src/store/variants/types';

import ButtonElement from 'editor/src/component/DesktopSidebar/TabContents/ProductTabContent/ProductControlContent/ProductControls/elements/ButtonElement';
import { ControlOptionProps } from 'editor/src/component/DesktopSidebar/TabContents/ProductTabContent/ProductControlContent/ProductControls/ProductControlProps';
import SingleValueProductControl from 'editor/src/component/DesktopSidebar/TabContents/ProductTabContent/ProductControlContent/ProductControls/single/SingleValueProductControl';

import buttonStyles from 'editor/src/component/DesktopSidebar/TabContents/ProductTabContent/ProductControlContent/ProductControls/single/SingleControl/index.module.scss';

interface Props {
  noTopBar?: boolean;
}

function ColorSpaceButton({ option, control, toggle, selected, disabled, unavailable }: ControlOptionProps) {
  const switchingToCMYK = useSelector(isSwitchingToCMYK);
  return (
    <ButtonElement
      option={option}
      control={control}
      toggle={toggle}
      selected={selected}
      disabled={disabled}
      unavailable={unavailable}
      loading={switchingToCMYK && option.value === ColorSpace.CMYK}
    />
  );
}

function ColorSpaceControl({ noTopBar }: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const designColorSpace = useSelector(getDesignColorSpace);
  const switchingToCMYK = useSelector(isSwitchingToCMYK);
  // A CMYK switch waiting for the print profile shows as chosen, which leaves sRGB clickable to cancel it.
  const colorSpace = switchingToCMYK ? ColorSpace.CMYK : designColorSpace;

  function onSelect(_: VariationProductControl, option: ProductControlOption) {
    void dispatch(setDesignColorSpaceOperation(option.value as ColorSpace));
  }

  const control: VariationProductControl = useMemo(
    () => ({
      type: 'single',
      uiType: ControlUIType.Button,
      key: 'print',
      title: t('Color space'),
      options: [
        { title: 'sRGB', value: ColorSpace.sRGB },
        { title: 'CMYK', value: ColorSpace.CMYK },
      ],
    }),
    [],
  );

  return (
    <SingleValueProductControl
      noTopBar={noTopBar}
      className={cn(buttonStyles.ButtonControl, 'cy-colorspace-control')}
      title={control.title}
      control={control}
      options={control.options}
      toggleOption={onSelect}
      selectedValue={colorSpace}
      Element={ColorSpaceButton}
    />
  );
}

export default React.memo(ColorSpaceControl);
